import { createMusicDirector } from './music/index.js';

/** Local synthesis only. Call unlock from a trusted pointer or keyboard gesture. */
export function createAudio() {
  let music;
  let scene = { cue: 'title', era: 'occupied', intensity: 0.4 };
  let intensity = 0.4;
  let context,
    master,
    ambience,
    muted = false,
    paused = false;
  try {
    muted = globalThis.localStorage?.getItem('ttg:muted') === 'true';
  } catch {
    /* Optional storage. */
  }
  const apply = () => {
    if (master) master.gain.setTargetAtTime(muted || paused ? 0 : 0.2, context.currentTime, 0.08);
  };
  const unlock = () => {
    try {
      if (!context) {
        const AudioContext = globalThis.AudioContext ?? globalThis.webkitAudioContext;
        if (!AudioContext) return;
        context = new AudioContext();
        master = context.createGain();
        master.gain.value = 0;
        master.connect(context.destination);
        const buffer = context.createBuffer(1, context.sampleRate * 3, context.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
        ambience = context.createBufferSource();
        ambience.buffer = buffer;
        ambience.loop = true;
        const wind = context.createBiquadFilter();
        wind.type = 'lowpass';
        wind.frequency.value = 480;
        const windGain = context.createGain();
        windGain.gain.value = 0.18;
        ambience.connect(wind);
        wind.connect(windGain);
        windGain.connect(master);
        const rain = context.createBiquadFilter();
        rain.type = 'highpass';
        rain.frequency.value = 5000;
        const rainGain = context.createGain();
        rainGain.gain.value = 0.035;
        ambience.connect(rain);
        rain.connect(rainGain);
        rainGain.connect(master);
        ambience.start();
        const machine = context.createOscillator();
        machine.type = 'triangle';
        machine.frequency.value = 57;
        const machineGain = context.createGain();
        machineGain.gain.value = 0.045;
        machine.connect(machineGain);
        machineGain.connect(master);
        machine.start();
        music = createMusicDirector({ context, destination: master });
        music.setMuted(muted);
        music.setCue(scene.cue);
        music.setEra(scene.era);
        music.setIntensity(scene.intensity);
      }
      context.resume()?.catch(() => {});
      apply();
    } catch {
      /* Audio never gates play. */
    }
  };
  const cue = (name) => {
    if (!context || muted || paused || context.state !== 'running') return;
    const notes = {
      footstep: [90, 0.045],
      interact: [370, 0.12],
      objective: [740, 0.25],
      comms: [880, 0.08],
    };
    const [frequency, duration] = notes[name] ?? notes.interact;
    const tone = context.createOscillator(),
      gain = context.createGain(),
      now = context.currentTime;
    tone.frequency.setValueAtTime(frequency, now);
    tone.frequency.exponentialRampToValueAtTime(frequency * 0.65, now + duration);
    gain.gain.setValueAtTime(0.17, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    tone.connect(gain);
    gain.connect(master);
    tone.start();
    tone.stop(now + duration);
    tone.onended = () => {
      tone.disconnect();
      gain.disconnect();
    };
  };
  return {
    unlock,
    cue,
    setScene(value) {
      scene = value;
      intensity = value.intensity;
      music?.setCue(value.cue);
      music?.setEra(value.era);
      music?.setIntensity(value.intensity);
    },
    setIntensity(value) {
      if (Math.abs(value - intensity) < 0.03) return;
      intensity = value;
      music?.setIntensity(value);
    },
    stinger(id) {
      if (!muted && !paused) music?.stinger(id);
    },
    get muted() {
      return muted;
    },
    setPaused(value) {
      paused = value;
      apply();
    },
    toggleMute() {
      muted = !muted;
      music?.setMuted(muted);
      apply();
      try {
        globalThis.localStorage?.setItem('ttg:muted', String(muted));
      } catch {
        /* Optional storage. */
      }
      return muted;
    },
    dispose() {
      music?.dispose();
      music = null;
      context?.close()?.catch(() => {});
      context = null;
      master = null;
    },
  };
}

export function haptic(duration = 18) {
  try {
    globalThis.navigator?.vibrate?.(duration);
  } catch {
    /* Optional hardware. */
  }
}
